import React, { useCallback, useEffect, useRef } from "react";
import { Form, Row, Col, Button as AntdButton } from "antd";
import { Button } from "components/Buttons/Buttons";
import Modal from "components/Modal/Modal";
import {
    FormDatePicker,
    FormInput,
    FormInputArea,
    FormInputNumber,
    FormSelect,
    FormSwitch,
    FormHiddenSubmitButton,
} from "components/FormItems/FlatFormItems";
import { useForm } from "antd/lib/form/Form";
import moment from "moment";
import { useState } from "react";
import DealImageUpload from "./DealImageUpload";
import { useDispatch } from "react-redux";
import { DEAL_DISCOUNT_TYPE } from "utils/enums";
import { createDeal, getDeal, updateDeal } from "services/deals.service";
import { setLoading } from "redux/actions/app.actions";

const discountTypes = Object.entries(DEAL_DISCOUNT_TYPE).map(([label, value]) => ({
    label,
    value,
}));

export default function DealsAddEditModal({
    categories,
    isCategoriesLoading,
    data = {},
    visible,
    onCancel,
    onSuccess,
}) {
    const [form] = useForm();
    const submitRef = useRef();
    const dispatch = useDispatch();

    const [base64, setBase64] = useState("");
    const [file, setFile] = useState(null);

    const discountType = Form.useWatch("discountType", form);

    const isEdit = data?.dealId !== undefined;

    const loadData = () => {
        dispatch(getDeal(data?.dealId)).then((res) => {
            if (res?.status === "success") {
                form.setFieldsValue({
                    ...res?.data,
                    discountType: res?.data?.discountType || null,
                    startDate: res?.data?.startDate ? moment(res?.data?.startDate) : undefined,
                    endDate: res?.data?.endDate ? moment(res?.data?.endDate) : undefined,
                });
                setBase64(res?.data?.imageUrl);
            }
        });
    };

    useEffect(() => {
        if (visible && isEdit) {
            form.setFieldsValue({
                ...data,
                discountType: data?.discountType || null,
                startDate: data?.startDate ? moment(data?.startDate) : undefined,
                endDate: data?.endDate ? moment(data?.endDate) : undefined,
            });
            setBase64(data?.imageUrl);
            loadData();
        } else {
            form.resetFields();
            setBase64("");
            setFile(null);
        }
    }, [visible]);

    const handleSubmit = useCallback(
        async (values) => {
            dispatch(setLoading(true))
            const submittableValues = {
                ...values,
                discountType: values?.discountType ?? 0,
                discountValue: values?.discountValue ?? null,
                startDate: values?.startDate?.format("YYYY-MM-DD") ?? null,
                endDate: values?.endDate?.format("YYYY-MM-DD") ?? null,
                imageBase64String: base64?.split?.("base64,")?.[1],
                imageExt: file?.type?.split("/")[1],
            };

            let result = null;
            if (isEdit) {
                result = await dispatch(updateDeal(data.dealId, submittableValues));
            } else {
                result = await dispatch(createDeal(submittableValues));
            }

            if (result?.status === "success") {
                onSuccess?.(submittableValues);
            }
        },
        [data, isEdit, onSuccess, base64, file]
    );

    return (
        <Modal
            className="ant-modal-title-tradeGothic"
            destroyOnClose={true}
            width="720px"
            title={isEdit ? "Edit Deal" : "Create Deal"}
            onCancel={() => onCancel()}
            visible={visible}
            centered={true}
            footer={
                <div className="d-flex justify-between align-center">
                    <AntdButton type="text" onClick={() => onCancel()} style={{ padding: 0 }}>
                        Cancel
                    </AntdButton>
                    <Button
                        style={{ height: 50 }}
                        rounded={true}
                        onClick={() => submitRef.current?.click?.()}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <Form
                form={form}
                initialValues={{
                    isActive: true,
                }}
                layout="vertical"
                requiredMark={false}
                preserve={false}
                onFinish={handleSubmit}
                onFinishFailed={({ errorFields }) => {
                    form.scrollToField(errorFields[0].name, {
                        scrollMode: "if-needed",
                        block: "center",
                        behavior: "smooth",
                    });
                }}
            >
                <Row gutter={[24, 16]}>
                    <Col xs={24}>
                        <FormInput type="hidden" name="imageUrl" formItemStyles={{ height: 1, margin: 0 }} />
                        {visible && (
                            <DealImageUpload
                                isEdit={isEdit}
                                base64={base64}
                                setBase64={setBase64}
                                setFile={setFile}
                            />
                        )}
                    </Col>
                    <Col xs={24}>
                        <FormInput
                            variant="underlined"
                            name="headline"
                            label="Headline"
                            placeholder="Deal Headline"
                            required
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24}>
                        <FormInputArea
                            variant="underlined"
                            name="shortDescription"
                            label="Short Description"
                            placeholder="Short Description"
                            required
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            variant="underlined"
                            name="categoryId"
                            label="Category"
                            placeholder="Select category"
                            formItemStyles={{ marginBottom: 0 }}
                            options={categories?.filter(
                                (it) => it?.isActive || it?.categoryId === data?.categoryId
                            )}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase?.().includes?.(input.toLowerCase())
                            }
                            renderLabel="categoryName"
                            renderValue="categoryId"
                            required
                            loading={isCategoriesLoading}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormSelect
                            name="discountType"
                            variant="underlined"
                            label="Discount Type"
                            placeholder="Select Discount Type"
                            options={discountTypes}
                            renderLabel="label"
                            renderValue="value"
                            allowClear
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormInputNumber
                            name="discountValue"
                            variant="underlined"
                            label={
                                discountType === DEAL_DISCOUNT_TYPE["Discount Percentage"]
                                    ? "Discount Value (%)"
                                    : "Discount Value ($)"
                            }
                            placeholder="Discount Value"
                            min={0}
                            max={
                                discountType === DEAL_DISCOUNT_TYPE["Discount Percentage"]
                                    ? 100
                                    : undefined
                            }
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormDatePicker
                            form={form}
                            name="startDate"
                            label="Start Date"
                            variant="underlined"
                            formItemStyles={{ marginBottom: 0 }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <FormDatePicker
                            validateFirst
                            form={form}
                            name="endDate"
                            label="End Date"
                            variant="underlined"
                            formItemStyles={{ marginBottom: 0 }}
                            rules={[
                                {
                                    message: "End Date cannot be before Start Date",
                                    validator: (_, value) => {
                                        const startDate = form.getFieldValue("startDate");
                                        if (!value || !startDate) return Promise.resolve();
                                        if (value.isBefore(startDate, "day")) {
                                            return Promise.reject();
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        />
                    </Col>
                    <Col xs={24}>
                        <FormSwitch name="isActive" label="Active" />
                    </Col>
                </Row>
                <FormHiddenSubmitButton submitRef={submitRef} />
            </Form>
        </Modal>
    );
}
